import { useSyncExternalStore } from 'react';
import moment from 'moment';
import { ConnectionState } from './utils';

const UPDATE_SNOOZE_MS = 3600000; // 1 hour
export const MIN_AUTO_UPDATE_DELAY_MS = 60000; // 1 min
export const MAX_AUTO_UPDATE_DELAY_MS = 900000; // 15 min

export const APP_VERSION = import.meta.env.VITE_CURRENT_VERSION || 'DEV';

/**
 * Returns a random auto-update delay between 1 and 15 minutes, so kiosks spread their reloads after a
 * deploy instead of all reloading at once.
 */
export const getRandomAutoUpdateDelayMs = (
  random: () => number = Math.random,
) =>
  MIN_AUTO_UPDATE_DELAY_MS +
  Math.floor(
    random() * (MAX_AUTO_UPDATE_DELAY_MS - MIN_AUTO_UPDATE_DELAY_MS + 1),
  );

/**
 * Client-only application status, updated from outside React by SignalR and the update timers.
 */
export type AppStatus = {
  connectionState: ConnectionState;
  /** SignalR failed to connect several times in a row */
  connectionFailed: boolean;
  updatePromptOpen: boolean;
  autoUpdateTime?: Date;
};

const initialStatus: AppStatus = {
  connectionState: 'disconnected',
  connectionFailed: false,
  updatePromptOpen: false,
};

let status = initialStatus;
let autoUpdateTimeoutId: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

const setStatus = (update: Partial<AppStatus>) => {
  status = { ...status, ...update };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Hook that returns the current application status
 */
export const useAppStatus = () => useSyncExternalStore(subscribe, () => status);

/**
 * Returns the current application status outside React
 */
export const getAppStatus = () => status;

/**
 * Restores the initial status and cancels pending reloads. For tests.
 */
export const resetAppStatus = () => {
  clearTimeout(autoUpdateTimeoutId);
  autoUpdateTimeoutId = undefined;
  status = initialStatus;
};

/**
 * Sets the SignalR connection state
 * @param connectionState
 */
export const setConnectionState = (connectionState: ConnectionState) =>
  setStatus({ connectionState });

/**
 * Sets whether SignalR failed to connect several times in a row
 * @param connectionFailed
 */
export const setConnectionFailed = (connectionFailed: boolean) =>
  setStatus({ connectionFailed });

/**
 * Schedules the update reload. Without a delay, reloads right away.
 * @param delay
 */
export const scheduleUpdate = (delay?: number) => {
  // Only one reload timer at a time, so the countdown matches the reload that actually happens.
  clearTimeout(autoUpdateTimeoutId);
  const now = moment();
  autoUpdateTimeoutId = setTimeout(() => {
    // eslint-disable-next-line no-restricted-globals
    location.reload();
  }, delay);
  if (delay) {
    setStatus({ autoUpdateTime: now.add(delay, 'ms').toDate() });
  }
};

/**
 * Closes the update prompt and reopens it after the snooze (1hr)
 */
export const remindMeLater = () => {
  clearTimeout(autoUpdateTimeoutId);
  setStatus({ updatePromptOpen: false });
  setTimeout(() => {
    setStatus({ updatePromptOpen: true });
    scheduleUpdate(getRandomAutoUpdateDelayMs());
  }, UPDATE_SNOOZE_MS);
};

/**
 * Compares the version from the server against the version embedded in the bundle,
 * and prompts for an update when they differ
 * @param newVersion
 */
export const checkVersion = (newVersion: string) => {
  const currentVersion = import.meta.env.VITE_CURRENT_VERSION;
  // no version info, must be in dev
  if (!currentVersion) {
    return;
  }
  // already up to date, do nothing
  if (currentVersion === newVersion) {
    return;
  }
  // The hub resends the version on every SignalR reconnect. Once an update is scheduled (or snoozed),
  // ignore repeats so they don't add earlier reload timers and undo the stagger.
  if (autoUpdateTimeoutId !== undefined) {
    return;
  }
  // new version detected, prompt user for update
  setStatus({ updatePromptOpen: true });
  scheduleUpdate(getRandomAutoUpdateDelayMs());
};
